import type { Message } from 'ai';
import React, { useCallback } from 'react';
import { classNames } from '~/utils/classNames';
import { AssistantMessage } from './AssistantMessage';
import { UserMessage } from './UserMessage';
import UserIcon from '~/assets/user.svg';
import type { PreviewError } from '~/lib/stores/previews';
import { ErrorCard } from '~/components/workbench/ErrorCard';
import { workbenchStore } from '~/lib/stores/workbench';
import { useStore } from '@nanostores/react';
import { groupAndCountErrors, type GroupedError } from '~/utils/errorUtils'; // Or wherever you place it

interface MessagesProps {
  id?: string;
  className?: string;
  isStreaming?: boolean;
  messages?: Message[];
  selectedFramework?: string;
}

export const Messages = React.forwardRef<HTMLDivElement, MessagesProps>((props: MessagesProps, ref) => {
  const { id, isStreaming = false, messages = [], selectedFramework } = props;
  const previewErrorArray = useStore(workbenchStore.previewError); // Already an array from the store

  const handleFixError = useCallback((errorsToFix: PreviewError[]) => {
    // Receives the original full array
    if (!errorsToFix || errorsToFix.length === 0) {
      console.log('No errors to fix.');
      return;
    }

    const groupedForPrompt = groupAndCountErrors(errorsToFix);

    const errorDetails = groupedForPrompt
      .map((group, index) => {
        const { representativeError, count } = group;
        return `Unique Error ${index + 1} (occurred ${count} time${count > 1 ? 's' : ''}):
  Message: ${representativeError.message}
  Stack: ${representativeError.stack || 'Not available'}`;
      })
      .join('\n\n---\n\n');

    const errorFixPrompt = `I'm encountering the following errors:
${errorDetails}
  
Please analyze these errors and provide the necessary code fixes to address all of them. Focus on fixing the unique errors listed.`;

    const fixEvent = new CustomEvent('dualite-fix-error', {
      detail: { prompt: errorFixPrompt },
    });
    document.dispatchEvent(fixEvent);
    console.log('Dispatched error fix event for grouped errors:', errorFixPrompt);
  }, []);

  const handleCloseError = useCallback(() => {
    workbenchStore.clearError();
  }, []);

  console.log('PreviewError Array in Messages.client:', previewErrorArray);

  // useEffect(() => console.log('[refresh] messages from Messages.client: ', messages), []);

  return (
    <div id={id} ref={ref} className={props.className}>
      {messages.length > 0
        ? messages.map((message, index) => {
            const { role, content, parts } = message;
            const isUserMessage = role === 'user';
            const isFirst = index === 0;
            const isLast = index === messages.length - 1;

            // extract reasoning text if any parts: [ 0:{type:'step-start'}, 1:{type:'reasoning', reasoning:string}, 2:{} ]
            const reasoningText = parts?.find((part) => part.type === 'reasoning')?.reasoning || '';

            return (
              <div
                key={index}
                className={classNames('gap-4 pt-4 px-4 w-full rounded-lg transition-colors', {
                  'bg-[#1A1A1A] backdrop-blur-sm': isUserMessage,
                  'bg-transparent': !isUserMessage,
                  'mt-4': !isFirst && isUserMessage,
                  hidden: selectedFramework?.toLowerCase() !== 'flutter' ? index === 2 : false,
                  flex: selectedFramework?.toLowerCase() !== 'flutter' ? index !== 2 : true,
                  'pb-4': isUserMessage,
                })}
              >
                {isUserMessage && (
                  <div className="flex items-center justify-center w-8 h-8 overflow-hidden text-gray-300 rounded-lg shrink-0 self-start">
                    {/* <div className="i-ph:user-fill text-lg"></div> */}
                    <img src={UserIcon} alt="User" className="w-8 p-1" />
                  </div>
                )}
                <div className="grid grid-col-1 w-full">
                  {isUserMessage ? (
                    <UserMessage content={content} messagesLength={messages.length} />
                  ) : (
                    <AssistantMessage
                      content={content}
                      index={index}
                      messagesLength={messages.length}
                      reasoningText={reasoningText}
                    />
                  )}
                </div>
              </div>
            );
          })
        : null}
      {isStreaming && <div className="text-center w-full text-gray-400 i-svg-spinners:3-dots-fade text-4xl mt-4"></div>}
      <ErrorCard
        error={previewErrorArray}
        onClose={handleCloseError}
        onFixError={handleFixError}
        isStreaming={isStreaming}
      />
    </div>
  );
});
